import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom';
import { useStore } from '../../store/store';
import { columnDefaultDisposition, ItemTypes, MASKS_TYPE, getPrimaryDark } from '../../constants/constants';
import { initializeBoxContent, isADrawBox, isATitleBox, reinitializeBoxWidthAndHeight } from '../../utils/boxes';
import { computeColumnWidth } from '../../utils/columns';
import { canPasteCopy } from '../../utils/copies';
import { gridUnit, sidebarWidth } from '../../constants/gridConfig';
import { useSingleOpenFloatingMenu } from '../../hooks';
import BoxButtonsWrapper from '../common/BoxButtonsWrapper';
const selector = (columnId, containerId) => ({
  addContainers,
  addCopiedContainer,
  updateColumnsInContainer,
  containers,
  gridConfiguration,
  configuration,
  currentDocument,
  boxes,
  landscape,
  copies
}) => {
  const container = columnId || containerId ? containers.find((c) => containerId ?
    c.id === containerId : c.columnsIds.includes(columnId))
    : undefined
  return {
    addContainers,
    addCopiedContainer,
    updateColumnsInContainer,
    container,
    gridConfiguration,
    configuration,
    titles: boxes.filter((box) => isATitleBox(box.type)),
    currentDocument,
    marginRight: configuration.margins.right * gridUnit,
    marginLeft: configuration.margins.left * gridUnit,
    landscape,
    copies
  }
};

// `onPick` bascule le menu en mode « remplir une cellule existante » : il rend
// la box choisie à l'appelant au lieu de créer une colonne ou une ligne. C'est
// ColumnWrapper qui insère alors, avec la même logique que le glisser-déposer
// (dimensionnement, confirmation de remplacement).
const AdderMenu = ({ containerIndex, closeMenu, columnId, columnIndex, containerId, fromViewer, anchorRef, onPick }) => {
  const { addContainers, addCopiedContainer, updateColumnsInContainer, container, gridConfiguration, configuration, titles, landscape, marginLeft, marginRight, copies } = useStore(selector(columnId, containerId));
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const previewRenderState = useStore(({ previewRenderState }) => previewRenderState)
  const isTemplate = useStore(({ currentDocument }) => currentDocument?.type === 'template')
  const accentColor = getPrimaryDark(isTemplate)
  const appendPages = useStore(({ appendPages }) => appendPages)
  const ref = React.useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: null, left: null });
  useSingleOpenFloatingMenu(closeMenu);

  const onAdderClick = ({ type, config, columnId, fromCopied = false, defaultValues = {}, copy }) => {
    let index;
    if (containerIndex === 0) { index = 0 } else {
      index = containerIndex
    }
    // Le presse-papiers contient jusqu'à MAX_COPIES éléments : c'est la vignette
    // cliquée qui désigne celui à coller, pas simplement le dernier copié.
    const copiedItem = copy || copies[0]?.item;
    if (fromCopied && copiedItem?.type === ItemTypes.CONTAINER_COPIED) {
      // Grille (colonnes ET lignes imbriquées) + box reposées d'un bloc, côté
      // store : ici on ne sait rien des lignes.
      addCopiedContainer({ index, copy: copiedItem })
      closeMenu();
      return
    }
    const box = (copiedItem && fromCopied) ? { ...copiedItem } : { type, ...config[type], copied: false }

    if (columnId || containerId) {
      updateColumnsInContainer({
        columnsSizes: columnDefaultDisposition[container.columnsIds.length + 1],
        index: columnIndex,
        containerId: container.id,
        newBoxes: [box]
      });
      closeMenu();
      return;
    }

    let newBox = initializeBoxContent({
      box,
      configuration,
      titles,
    });
    newBox = { ...newBox, ...defaultValues }
    const drawMode = isADrawBox(box.type);

    if (!drawMode) {
      newBox = reinitializeBoxWidthAndHeight({
        box: newBox,
        columnWidth: computeColumnWidth({
          landscape,
          marginRight,
          marginLeft
        }),
        onCreate: true,
        header,
        footer
      })
    }

    addContainers({
      index,
      drawMode,
      box: newBox,
      callback: () => {
        if (previewRenderState) {
          appendPages(containerIndex)
        }
      }
    })
    closeMenu();
  }

  const onClick = ({ type, fromCopied = false, defaultValues = {}, copy }) => {
    if (onPick) {
      const copiedItem = copy || copies[0]?.item;
      // Même forme que l'item de drag d'une vignette (cf. BoxButton) : c'est ce
      // qu'attend le chemin d'insertion côté cellule.
      const box = (fromCopied && copiedItem)
        ? { ...copiedItem }
        : { type, ...gridConfiguration[type], copied: false, ...defaultValues };
      onPick(box);
      closeMenu();
      return;
    }
    onAdderClick({ type, config: gridConfiguration, columnId, fromCopied, defaultValues, copy })
  }
  useLayoutEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const anchorCenterX = rect.left + rect.width / 2;
      setMenuPosition({ top: rect.bottom, left: anchorCenterX - 125 });
    }
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (ref.current && menuPosition.top !== null && menuPosition.left !== null) {
      const menuRect = ref.current.getBoundingClientRect();
      const maxRight = window.innerWidth - sidebarWidth;
      let newLeft = menuPosition.left;
      let newTop = menuPosition.top;

      if (newLeft + menuRect.width > maxRight) {
        newLeft = maxRight - menuRect.width;
      }
      if (newLeft < 0) newLeft = 0;

      if (newTop + menuRect.height > window.innerHeight) {
        newTop = window.innerHeight - menuRect.height;
      }

      if (newLeft !== menuPosition.left || newTop !== menuPosition.top) {
        setMenuPosition({ top: newTop, left: newLeft });
      }
    }
  }, [menuPosition]);

  if (menuPosition.top === null || menuPosition.left === null) return null;

  return createPortal(
    <div
      ref={ref}
      data-outside-ignore="true"
      style={{
        position: "fixed",
        top: menuPosition.top,
        left: menuPosition.left,
        padding: 16,
        boxShadow: "rgb(94, 94, 94) 0px 0px 15px -4.6px",
        display: "flex",
        flexDirection: "column",
        gap: 32,
        borderRadius: 12,
        width: 250,
        zIndex: 10001,
        // Bleu marine du thème (logo) : cadre les boutons blancs et raccord avec
        // la barre flottante. Theme-aware (navy proposition / indigo template).
        backgroundColor: accentColor
      }}>
      <div style={{
        display: "flex", alignItems: "baseline", whiteSpace: "nowrap", gap: 10, flexDirection: "column"
      }}>
        <div style={{ fontSize: 15, display: "flex", justifyContent: "center", width: "100%", color: '#FFFFFF' }}>
          {onPick ? "Ajouter un élément" : columnId ? "Ajouter une colonne" : "Ajouter une ligne"}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <BoxButtonsWrapper onClick={onClick} fromViewer={fromViewer}
            showCopy={!fromViewer && copies.some(({ item }) => canPasteCopy(item, { columnId }))}
            fromAdder={true}
            dividerColor={'#FFFFFF'}
            columnId={columnId}
            canDrag={false} />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AdderMenu
